'use client';

import './share-buttons.scss';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';

type ShareButtonsProp = {
  shareUrl?: string;
  shareTitle?: string;
  shareSummary?: string;
  size?: 'sm' | 'md' | 'lg';
  buttonLabel?: string;
};
export const ShareButtons: React.FC<ShareButtonsProp> = ({
  shareUrl,
  shareTitle,
  shareSummary,
  size = 'md',
  buttonLabel,
}) => {
  const { t } = useTranslation(['core']);
  const [open, setOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { NEXT_PUBLIC_BASE_URL_DOTCOM } = getPublicConfig();
  const pathname = usePathname();

  const shareLink =
    shareUrl ||
    `${NEXT_PUBLIC_BASE_URL_DOTCOM}${pathname}` ||
    `${NEXT_PUBLIC_BASE_URL_DOTCOM}`;

  const [copyButtonText, setCopyButtonText] = useState<string>(() =>
    t('core:sharing.copy'),
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch (errors) {
      setCopyButtonText(String(errors));
      setTimeout(() => {
        setCopyButtonText(t('core:sharing.copy'));
      }, 1000);
      return;
    } finally {
      (
        document.querySelector('input[name=shareURL]') as HTMLInputElement
      ).select();
      setCopyButtonText(t('core:sharing.copied'));
      setTimeout(() => {
        setCopyButtonText(t('core:sharing.copy'));
      }, 1000);
    }
  };

  return (
    <div className="ShareButtons">
      <Dialog
        open={open}
        onOpenChange={async (tryToOpen) => {
          if (tryToOpen) {
            const nativeShareData = {
              title: shareTitle || document.title,
              text: shareSummary || '',
              url: shareLink,
            };
            if (
              navigator.maxTouchPoints > 1 &&
              navigator.canShare &&
              navigator.canShare(nativeShareData)
            ) {
              trackClick('native share sheet', shareLink);
              try {
                await navigator.share(nativeShareData);
              } catch (error) {
                if ((error as DOMException).name === 'AbortError') {
                  setOpen(false);
                } else {
                  trackClick('custom share sheet', shareLink);
                  setOpen(true);
                }
              }
            } else {
              trackClick('custom share sheet', shareLink);
              setOpen(true);
            }
          } else {
            setOpen(false);
          }
        }}
      >
        <DialogTrigger>
          <ButtonStyle
            size={size}
            iconBefore={<ArkIcon iconName="share-02" />}
            purpose="neutral"
            variant="secondary"
          >
            <button type="button">
              {buttonLabel || t('core:actions.share')}
            </button>
          </ButtonStyle>
        </DialogTrigger>
        <DialogContent
          title={t('core:sharing.share')}
          style={{ textAlign: 'center' }}
        >
          <div className="ShareButtons__modal flex flex-col gap-comfy-xl">
            <div className="flex flex-row gap-comfy-lg align-center">
              <button
                type="button"
                onClick={async () => {
                  trackClick('facebook - share', shareLink);
                  const facebookPost = `https://www.facebook.com/sharer/sharer.php?u=${shareLink}`;
                  window
                    .open(facebookPost, '_blank', 'noopener,noreferrer')
                    ?.focus();
                }}
              >
                <ArkIcon iconName="facebook" alt={t('core:sharing.facebook')} />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('twitter - share', shareLink);
                  const twitterTitle = encodeURIComponent(
                    shareTitle || document.title,
                  );
                  const tweet = `https://twitter.com/intent/tweet?url=${shareLink}&text=${twitterTitle}&via=compassion`;
                  window.open(tweet, '_blank', 'noopener,noreferrer')?.focus();
                }}
              >
                <ArkIcon iconName="twitter" alt={t('core:sharing.twitter')} />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('linkedin - share', shareLink);
                  const linkedinPost = `https://www.linkedin.com/cws/share?url=${shareLink}`;
                  window
                    .open(linkedinPost, '_blank', 'noopener,noreferrer')
                    ?.focus();
                }}
              >
                <ArkIcon iconName="linkedin" alt={t('core:sharing.linkedin')} />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('email - share', shareLink);
                  const emailSubject = encodeURIComponent(
                    shareTitle || document.title,
                  );
                  const emailDescription = encodeURIComponent(
                    shareSummary || '',
                  );
                  const emailBody = `${emailDescription}%20${shareLink}`;
                  const email = `mailto:?subject=${emailSubject}&body=${emailBody}`;
                  window.open(email, '_blank', 'noopener,noreferrer')?.focus();
                }}
              >
                <ArkIcon iconName="mail-01" alt={t('core:sharing.email')} />
              </button>
            </div>
            <div className="flex flex-row align-center gap-snug-md">
              <div className="InputField">
                <input
                  name="shareURL"
                  className="form-control"
                  aria-label={t('core:sharing.copy')}
                  type="text"
                  size={255}
                  value={shareLink}
                  readOnly
                  onClick={async () => {
                    copyLink();
                  }}
                />
              </div>
              <ButtonStyle size="md">
                <button
                  type="button"
                  onClick={async () => {
                    copyLink();
                  }}
                >
                  {copyButtonText}
                </button>
              </ButtonStyle>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
